import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone, Paperclip, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { openFile } from "@/lib/download";
import { fmtDate, fmtEUR, nowPeriod, ymStr } from "@/lib/format";
import { REGIME_SHORT } from "@/lib/fiscale";
import { useAuth } from "@/context/AuthContext";
import { MarcaSelect, useMarche } from "@/components/app/MarcaSelect";
import { GENERALE, SpesaBrandDialog } from "@/components/app/SpesaBrandDialog";
import { ChannelBadge, ConfirmDialog, EmptyState, Hint, Loading, PageHeader, PeriodSelect, SimpleSelect, btnPrimary } from "@/components/app/common";

export default function SpeseBrand() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const marche = useMarche();
  const [period, setPeriod] = useState({ ...nowPeriod(), mese: null });
  const [marca, setMarca] = useState("all");
  const [cat, setCat] = useState("all");
  const [data, setData] = useState(null);
  const [categorie, setCategorie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [edit, setEdit] = useState({ open: false, item: null });
  const [del, setDel] = useState(null);

  const loadCats = useCallback(() => api.get("/categorie", { params: { tipo: "spesa_brand" } }).then((r) => setCategorie(r.data)), []);
  const load = useCallback(() => {
    const params = { ...(period.mese ? { mese: ymStr(period.anno, period.mese) } : { anno: period.anno }), ...(marca !== "all" ? { marca_id: marca === GENERALE ? "generale" : marca } : {}), ...(cat !== "all" ? { categoria: cat } : {}) };
    api.get("/spese-brand", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
    loadCats();
  }, [period, marca, cat, loadCats]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/fornitori").then((r) => setFornitori(r.data.items || r.data)).catch(() => {}); }, []);

  const remove = async () => {
    try { await api.delete(`/spese-brand/${del.id}`); toast.success("Spesa eliminata"); setDel(null); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="spese-brand-page">
      <PageHeader overline="Costi" title="Spese brand" subtitle="Spese una tantum per gestire e far crescere le marche: shooting, fiere, campagne, grafica, registrazioni…">
        <Button className={btnPrimary} onClick={() => setEdit({ open: true, item: null })} data-testid="spesa-add-button"><Plus className="h-4 w-4 mr-1" />Nuova spesa</Button>
      </PageHeader>
      <div className="mb-5"><Hint testId="spese-brand-hint">Ogni spesa crea un costo nella categoria <b>Spese brand</b> (con la sua sottocategoria): riduce l'utile, compare nel grafico costi della Dashboard e nell'IVA a credito. In Analisi margini riduce il <b>margine netto marca</b>; le spese «Tutte le marche / generale» restano separate e non vengono ripartite.</Hint></div>
      <div className="flex flex-wrap gap-2 mb-5">
        <PeriodSelect anno={period.anno} mese={period.mese} onChange={setPeriod} allowYear prefix="spese-brand" />
        <MarcaSelect marche={[{ id: GENERALE, nome: "Tutte le marche / generale", attivo: true }, ...marche]} value={marca} onChange={setMarca} testId="spese-brand-marca-select" />
        <SimpleSelect testId="spese-brand-categoria-select" className="w-52" value={cat} onChange={setCat} options={[{ value: "all", label: "Tutte le categorie" }, ...categorie.map((c) => ({ value: c.nome, label: c.nome }))]} />
      </div>
      {!data ? <Loading /> : !data.items.length ? (
        <EmptyState icon={Megaphone} testId="spese-brand-empty" title="Nessuna spesa brand nel periodo" text="Registra shooting, fiere, campagne influencer e altre spese legate a una marca per vederne il ritorno in Analisi margini." />
      ) : (
        <div className="card-soft overflow-x-auto" data-testid="spese-brand-table">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#FAF7F5] text-[11px] uppercase tracking-wider text-[#756B64]">
              <tr>{["Data", "Marca", "Categoria", "Descrizione", "Canale", "Fornitore", "Imponibile", ""].map((h, i) => <th key={i} className={`font-semibold px-4 py-3 ${h === "Imponibile" ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
            </thead>
            <tbody className="divide-y divide-[#EAE3DC]">
              {data.items.map((s, i) => (
                <tr key={s.id} data-testid={`spesa-row-${i}`} className="hover:bg-[#FAF7F5]">
                  <td className="px-4 py-3 whitespace-nowrap">{fmtDate(s.data)}</td>
                  <td className="px-4 py-3 font-medium">{s.marca}</td>
                  <td className="px-4 py-3"><span className="text-xs rounded-full px-2 py-0.5 bg-[#E8C5C8]/50 text-[#77594E]">{s.categoria}</span></td>
                  <td className="px-4 py-3 text-[#5E4A42]">{s.descrizione || "—"}{s.file && <button onClick={() => openFile(s.file.file_id)} className="ml-2 inline-flex text-[#8C6D62]" title={s.file.filename} data-testid={`spesa-file-${i}`}><Paperclip className="h-3.5 w-3.5" /></button>}</td>
                  <td className="px-4 py-3"><ChannelBadge canale={s.canale} /></td>
                  <td className="px-4 py-3 text-[#756B64]">{s.fornitore || "—"}{s.regime !== "ordinaria" && <span className="ml-1 text-[10px]">({REGIME_SHORT[s.regime]})</span>}</td>
                  <td className="px-4 py-3 text-right tabular font-semibold">{fmtEUR(s.imponibile)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button className="p-1.5 rounded-full hover:bg-[#F4EFEA]" onClick={() => setEdit({ open: true, item: s })} data-testid={`spesa-edit-${i}`}><Pencil className="h-4 w-4" /></button>
                    <button className="p-1.5 rounded-full hover:bg-[#E07A5F]/15 text-[#B4553D]" onClick={() => setDel(s)} data-testid={`spesa-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#F4EFEA] font-semibold"><tr><td className="px-4 py-3" colSpan={6}>Totale {isAdmin ? "" : "(le tue spese)"}</td><td className="px-4 py-3 text-right tabular" data-testid="spese-brand-totale">{fmtEUR(data.totale)}</td><td /></tr></tfoot>
          </table>
        </div>
      )}
      <SpesaBrandDialog open={edit.open} item={edit.item} marche={marche} categorie={categorie} fornitori={fornitori} onOpenChange={(o) => setEdit((s) => ({ ...s, open: o }))} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Eliminare la spesa?" text="Verrà eliminato anche il relativo costo nei movimenti." onConfirm={remove} />
    </div>
  );
}
